<script setup>
import { ref } from 'vue'
import logo from '@/../images/logo.jpg'
import UserMale from '@/../images/user-male.png'
import { Link } from '@inertiajs/vue3'

const active = ref(false)
const mobileMenuOpen = ref(false)

const toggleUserDropdown = () => {
  active.value = !active.value
}

const closeDropdown = () => {
  active.value = false
}

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
  active.value = false
}
</script>

<template>
  <header class="w-full fixed top-0 left-0 z-50 bg-linear-to-br from-primary-700 to-primary-900 backdrop-blur-md shadow-lg">
    <nav
      class="w-[90vw] md:max-w-[85vw] mx-auto flex justify-between items-center py-3 transition-all duration-300"
    >
      <!-- Logo and Title -->
      <div class="flex items-center gap-3">
        <img :src="logo" alt="logo" class="w-7 h-7 bg-transparent" />
        <h1 class="text-xl md:text-2xl font-bold text-white tracking-tight">KIWAFSU</h1>
      </div>

      <!-- Desktop Nav -->
      <div class="hidden md:flex items-center gap-8 text-gray-100">
        <Link href="/staff/dashboard" class="hover:text-green-300 transition">Dashboard</Link>
        <Link href="/staff/students" class="hover:text-green-300 transition">Students</Link>
        <Link href="/staff/cas" class="hover:text-green-300 transition">CAs</Link>
        <Link href="/staff/exams" class="hover:text-green-300 transition">Exams</Link>
        <Link href="/staff/results" class="hover:text-green-300 transition">Reports</Link>
        <Link href="/staff/attendance" class="hover:text-green-300 transition">Attendance</Link>
      </div>

      <!-- Right Side -->
      <div class="flex items-center gap-4">
        <!-- Notifications -->
        <button class="hidden md:block relative text-gray-300 hover:text-white transition">
          <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"
            />
          </svg>
          <span
            class="absolute top-0 right-0 inline-block w-2.5 h-2.5 bg-red-600 border-2 border-white rounded-full"
          ></span>
        </button>

        <!-- Profile Dropdown -->
        <div class="relative">
          <button @click="toggleUserDropdown" class="focus:outline-none">
            <img
              :src="UserMale"
              alt="User"
              class="w-9 h-9 rounded-full object-cover border border-white/30"
            />
          </button>

          <!-- Overlay -->
          <div v-if="active" class="fixed inset-0 z-10" @click="closeDropdown"></div>

          <!-- Dropdown -->
          <transition name="fade">
            <ul
              v-if="active"
              class="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-100 z-30 overflow-hidden"
            >
              <li>
                <Link href="/profile" class="block px-4 py-2 text-gray-600 hover:bg-gray-100"
                  >Profile</Link
                >
              </li>
              <li>
                <Link href="/settings" class="block px-4 py-2 text-gray-600 hover:bg-gray-100"
                  >Settings</Link
                >
              </li>
              <li>
                <Link
                  method="POST"
                  as="button"
                  href="/logout"
                  class="block w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-100"
                  >Logout</Link
                >
              </li>
            </ul>
          </transition>
        </div>

        <!-- Mobile Menu Toggle -->
        <button
          @click="toggleMobileMenu"
          class="md:hidden p-2 rounded-lg text-white hover:bg-primary-600 transition-colors duration-200"
          :class="{ 'bg-primary-600': mobileMenuOpen }"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path
              v-if="!mobileMenuOpen"
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M4 6h16M4 12h16M4 18h16"
            />
            <path
              v-else
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </nav>

    <!-- Mobile Dropdown -->
    <transition name="slide-fade">
      <div
        v-if="mobileMenuOpen"
        class="absolute top-full left-0 w-full bg-white/95 backdrop-blur-md border-t border-gray-200 rounded-b-2xl shadow-2xl overflow-hidden md:hidden z-40"
      >
        <div class="flex flex-col items-center py-4 space-y-2 text-primary-700 font-semibold">
          <Link
            href="/staff/dashboard"
            class="block w-full px-6 py-3 text-center hover:bg-primary-50 transition"
            @click="mobileMenuOpen = false"
            >Dashboard</Link
          >
          <Link
            href="/staff/students"
            class="block w-full px-6 py-3 text-center hover:bg-primary-50 transition"
            @click="mobileMenuOpen = false"
            >Students</Link
          >
          <Link
            href="/staff/ca"
            class="block w-full px-6 py-3 text-center hover:bg-primary-50 transition"
            @click="mobileMenuOpen = false"
            >CAs</Link
          >
          <Link
            href="/staff/exams"
            class="block w-full px-6 py-3 text-center hover:bg-primary-50 transition"
            @click="mobileMenuOpen = false"
            >Exams</Link
          >
          <Link
            href="/staff/results"
            class="block w-full px-6 py-3 text-center hover:bg-primary-50 transition"
            @click="mobileMenuOpen = false"
            >Reports</Link
          >
          <Link
            href="/staff/attendance"
            class="block w-full px-6 py-3 text-center hover:bg-primary-50 transition"
            @click="mobileMenuOpen = false"
            >Attendance</Link
          >

          <hr class="w-4/5 border-primary-200 my-2" />

          <Link
            href="/profile"
            class="block w-full px-6 py-3 text-center hover:bg-primary-50 transition"
            @click="mobileMenuOpen = false"
            >Profile</Link
          >
          <Link
            href="/settings"
            class="block w-full px-6 py-3 text-center hover:bg-primary-50 transition"
            @click="mobileMenuOpen = false"
            >Settings</Link
          >
          <Link
            method="POST"
            as="button"
            href="/logout"
            class="block w-full px-6 py-3 text-center hover:bg-red-50 text-red-600 transition"
            @click="mobileMenuOpen = false"
            >Logout</Link
          >
        </div>
      </div>
    </transition>
  </header>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
